/**
 * NOLAS 2026, (c) Mikael (Mikael FOURNY). Fork de la plateforme TNE 2026.
 * src/i18n/fr.journee.ts
 *
 * Role :
 *   Sous-catalogue i18n de l ecran « Ma journee », le recapitulatif de fin de
 *   journee de l entreprise qui recrute. Fusionne dans `messagesFr` par
 *   `i18n/fr.ts`, donc utilisable comme n importe quelle cle.
 *
 * Pourquoi un fichier SEPARE plutot que d allonger `fr.ts` :
 *   `fr.ts` fait ~7 000 lignes et c est LA dependance partagee du depot.
 *   Plusieurs chantiers menes en parallele qui y ecrivent en meme temps se
 *   clobberaient. Un catalogue par chantier supprime la collision par
 *   construction ; `fr.ts` ne recoit qu un import et un etalement.
 *
 * ⚠ Regles tenues ici :
 *   • aucun nom d evenement, aucune date, aucun lieu en dur : ils viennent de
 *     la base (`{eventName}`, `{eventDate}`) ;
 *   • aucun mot du lexique configurable en dur (« atelier », « exposant »,
 *     « visiteur ») : jetons `{lexWorkshop*}`, `{lexExhibitor*}`,
 *     `{lexVisitor*}`. Un test echoue sinon ;
 *   • apostrophe typographique `’`, et jamais d apostrophe DROITE juste avant
 *     un `{token}` : en ICU, elle echapperait l accolade et ferait disparaitre
 *     le jeton du rendu ;
 *   • aucun tiret cadratin, nulle part.
 *
 *   Le vocabulaire retenu est celui du recrutement, pas celui de la
 *   plateforme : « personnes rencontrees », « coups de coeur », « CV recus ».
 *   Il ne depend d aucun role renommable, il vaut donc sur toutes les
 *   instances sans passer par le lexique.
 *
 *   2026-10-04 : les « rencontres » sont devenues les fiches d'entretien (un
 *   seul nom à l'écran) : six libellés repris ici. Mikael - chef de projet
 *
 * @author Mikael - chef de projet
 * @since  2026-09-10
 */

export const messagesJourneeFr: Record<string, string> = {
  // ── En-tete ────────────────────────────────────────────────────────────
  'maJournee.title': 'Ma journée',
  'maJournee.back': 'Mon stand',
  'maJournee.lead':
    'Le récapitulatif de votre journée : qui vous avez rencontré, vos coups de cœur, les CV reçus. Tout est exportable pour votre suivi.',
  'maJournee.checking': 'Vérification de votre accès…',
  'maJournee.loading': 'Calcul de votre journée…',
  'maJournee.notExhibitor': 'Cet espace est réservé aux entreprises inscrites.',
  'maJournee.error': 'Votre récapitulatif n’a pas pu être chargé.',
  'maJournee.error.hint':
    'Le serveur n’a pas répondu, ou sa réponse n’a pas le format attendu. Réessayez dans un instant.',
  'maJournee.retry': 'Réessayer',

  // ── Choix du jour ──────────────────────────────────────────────────────
  'maJournee.date.label': 'Journée affichée',
  'maJournee.date.hint':
    'Par défaut, aujourd’hui. Changez la date pour revoir une journée précédente.',

  // ── Chiffre principal ──────────────────────────────────────────────────
  'maJournee.people':
    '{count, plural, =0 {Aucune personne rencontrée} one {# personne rencontrée} other {# personnes rencontrées}}',
  'maJournee.people.hint':
    '{count, plural, =0 {} one {en # passage enregistré} other {en # passages enregistrés}}',

  // ── Indicateurs ────────────────────────────────────────────────────────
  'maJournee.stats.title': 'Le détail de la journée',
  'maJournee.stats.entretiens': 'Entretiens',
  'maJournee.stats.depotsCv': 'Dépôts de CV',
  'maJournee.stats.information': 'Demandes d’information',
  'maJournee.stats.autre': 'Autres échanges',
  'maJournee.stats.nonQualifies': 'Échanges non qualifiés',
  'maJournee.stats.interest': 'Intérêt moyen exprimé',
  'maJournee.stats.interest.value': '{value} sur 5',
  'maJournee.stats.interest.none': 'Non renseigné',
  'maJournee.stats.interest.hint':
    '{count, plural, =0 {Aucune note posée} one {sur # fiche d’entretien notée} other {sur # fiches d’entretien notées}}',
  'maJournee.stats.plage': 'Premier et dernier passage',
  'maJournee.stats.plage.value': '{debut} et {fin}',
  'maJournee.stats.heureForte': 'Heure la plus dense',
  'maJournee.stats.heureForte.value': '{heure} h',
  'maJournee.stats.heureForte.hint':
    '{count, plural, one {# passage} other {# passages}}',

  // ── Coups de coeur ─────────────────────────────────────────────────────
  'maJournee.favorites.title': 'Vos coups de cœur',
  'maJournee.favorites.lead':
    'Les personnes que vous avez notées {seuil} étoiles ou plus. C’est la liste courte à reprendre en premier.',
  'maJournee.favorites.empty.title': 'Aucun coup de cœur signalé aujourd’hui.',
  'maJournee.favorites.empty.hint':
    'Les étoiles se posent au moment du scan, ou plus tard depuis les « Fiches d’entretien ». Elles ne servent qu’à vous : personne d’autre ne les voit.',
  'maJournee.favorites.stars': '{count} étoiles sur 5',
  'maJournee.favorites.cv': 'CV reçu',
  'maJournee.favorites.noteTruncated': '(note complète dans la fiche d’entretien)',
  'maJournee.favorites.more':
    'Seules les {count} premières sont affichées. L’export les contient toutes.',

  // ── Types d echange ────────────────────────────────────────────────────
  'maJournee.type.entretien': 'Entretien',
  'maJournee.type.depot_cv': 'Dépôt de CV',
  'maJournee.type.information': 'Demande d’information',
  'maJournee.type.autre': 'Autre échange',

  // ── CV ─────────────────────────────────────────────────────────────────
  'maJournee.cv.title': 'Les CV de la journée',
  'maJournee.cv.scanned':
    '{count, plural, =0 {Aucun CV parmi les personnes rencontrées} one {# CV parmi les personnes rencontrées} other {# CV parmi les personnes rencontrées}}',
  'maJournee.cv.applications':
    '{count, plural, =0 {Aucune candidature reçue} one {# candidature reçue} other {# candidatures reçues}}',
  'maJournee.cv.applications.hint':
    'Une candidature vous est adressée directement : elle arrive même un jour où personne n’est passé.',
  'maJournee.cv.open': 'Ouvrir la bannette',
  'maJournee.cv.disabled':
    'Le dépôt de CV est désactivé sur cet événement : cette page n’affiche donc aucun CV.',
  'maJournee.cv.logged':
    'Chaque consultation de CV est inscrite dans un journal que la personne concernée peut lire.',

  // ── Export ─────────────────────────────────────────────────────────────
  'maJournee.export': 'Exporter ma journée',
  'maJournee.export.hint':
    'Un fichier ouvrable dans un tableur : une ligne par fiche d’entretien, avec vos étoiles et vos notes.',

  // ── Journee vide ───────────────────────────────────────────────────────
  'maJournee.empty.title': 'Rien n’a encore été enregistré pour cette journée.',
  'maJournee.empty.hint':
    'Chaque badge scanné apparaît ici en quelques secondes. Si vous avez échangé sans scanner, vous pouvez encore compléter vos fiches d’entretien : le récapitulatif se mettra à jour.',
  'maJournee.empty.scan': 'Ouvrir le scanner',
  'maJournee.empty.meetings': 'Compléter mes fiches d’entretien',
};
